import type { GridApi, RowClassParams } from "ag-grid-community";
import { observer } from "mobx-react-lite";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import AddTraceToDatasetDialog from "./AddTraceToDatasetDialog";
import SelectionActionBar, { type TraceImportConfig } from "./SelectionActionBar";
import type { TraceGridRow } from "./types";
import { useTraceColumns } from "./useTraceColumns";
import { useTracesGridData } from "./useTracesGridData";
import { useTracesGridInteractions } from "./useTracesGridInteractions";
import { useTracesPagingPanel } from "./useTracesPagingPanel";
import { useResetTracesPageOnStructuredSearch } from "./useResetTracesPageOnStructuredSearch";
import { useTracesTableRefresh } from "./traces-table-refresh-context";
import { TracesSelectionProvider, useTracesSelectionStore } from "./traces-selection-context";

import { useStructuredSearchStore } from "@/components/StructuredSearch";
import { StructuredSearchInfiniteGridShell } from "@/components/ui/ag-grid-table/StructuredSearchInfiniteGridShell";
import { useTimezone } from "@/context/timezone-context";
import type { WorkflowInputVariableScope } from "@/queries/traces";
import { isWorkspaceWorkflowVersionPathname } from "@/utils/workspacePathMatchers";

interface TracesAgGridProps {
  workflowInputVariableScope?: WorkflowInputVariableScope;
  traceImportConfig?: TraceImportConfig;
}

const TracesAgGridContent = observer(
  ({ workflowInputVariableScope, traceImportConfig }: TracesAgGridProps) => {
    const { timezone } = useTimezone();
    const { pathname } = useLocation();
    const structuredSearchStore = useStructuredSearchStore();
    const selection = useTracesSelectionStore();
    const { gridContainerRef, pagingPanel } = useTracesPagingPanel();
    const [datasetTraceId, setDatasetTraceId] = useState<string | null>(null);

    useResetTracesPageOnStructuredSearch();

    const {
      contextMenuItems,
      currentPage,
      handlePaginationChanged,
      handleRowClick,
      handleSortChanged,
      pageSize,
      recentlyViewedTraceId,
    } = useTracesGridInteractions(row => setDatasetTraceId(row.trace_id));

    const isWorkflowEntityAnalytics = isWorkspaceWorkflowVersionPathname(pathname);
    const columns = useTraceColumns(timezone, contextMenuItems, {
      hideTraceName: isWorkflowEntityAnalytics,
      prioritizeStartTime: isWorkflowEntityAnalytics,
      selectionLimit: traceImportConfig?.selectionLimit,
    });
    const {
      datasource,
      hasCompletedInitialLoad,
      isTableLoading,
      setGridApiRef,
      handleManualRefresh,
      totalRows,
    } = useTracesGridData(workflowInputVariableScope);
    const tableRefresh = useTracesTableRefresh();

    useEffect(() => {
      tableRefresh.registerHandler(handleManualRefresh);
      return () => {
        tableRefresh.registerHandler(null);
      };
    }, [handleManualRefresh, tableRefresh]);

    useEffect(() => {
      tableRefresh.setIsTableLoading(isTableLoading);
    }, [isTableLoading, tableRefresh]);

    useEffect(() => {
      selection.clear();
    }, [
      selection,
      structuredSearchStore.q,
      structuredSearchStore.filters,
      structuredSearchStore.dateRange.from,
      structuredSearchStore.dateRange.to,
    ]);

    const gridApiRef = useRef<GridApi<TraceGridRow> | null>(null);
    const handleSetGridApiRef = useCallback(
      (api: GridApi<TraceGridRow>) => {
        if (!api) {
          return;
        }
        gridApiRef.current = api;
        setGridApiRef(api);
      },
      [setGridApiRef],
    );

    const selectedIdsKey = selection.selectedIds.join(",");
    useEffect(() => {
      gridApiRef.current?.redrawRows();
    }, [
      recentlyViewedTraceId,
      selection.mode,
      selection.count,
      selection.matchingLimit,
      selectedIdsKey,
    ]);

    const getRowClass = (params: RowClassParams<TraceGridRow>) => {
      if (params.node.detail) {
        return "!bg-white hover:!bg-white";
      }

      if (params.data?.trace_id && params.data.trace_id === recentlyViewedTraceId) {
        return "!bg-slate-50";
      }

      if (selection.mode === "all_matching") {
        const limit = selection.matchingLimit ?? selection.count;
        return params.node.rowIndex !== null && params.node.rowIndex < limit ? "!bg-blue-50" : "";
      }

      return params.data?.trace_id && selection.has(params.data.trace_id) ? "!bg-blue-50" : "";
    };

    return (
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <StructuredSearchInfiniteGridShell<TraceGridRow>
          gridContainerRef={gridContainerRef}
          pagingPanel={pagingPanel}
          columns={columns}
          datasource={datasource}
          contextMenuItems={contextMenuItems}
          currentPage={currentPage}
          pageSize={pageSize}
          hasCompletedInitialLoad={hasCompletedInitialLoad}
          isTableLoading={isTableLoading}
          loadingLabel="Loading traces..."
          loadingDescription="We are fetching the matching traces"
          emptyTitle="No traces found"
          emptyDescription="Try adjusting the current search filters"
          getRowId={params => params.data.trace_id}
          getRowClass={getRowClass}
          onRowClick={handleRowClick}
          onSortChanged={handleSortChanged}
          onPaginationChanged={handlePaginationChanged}
          getApiRef={handleSetGridApiRef}
          pagingPanelContent={
            <div className="order-first mr-auto flex h-full items-center">
              <SelectionActionBar totalMatching={totalRows} traceImportConfig={traceImportConfig} />
            </div>
          }
          showEmptyState={hasCompletedInitialLoad && !isTableLoading && totalRows === 0}
        />
        <AddTraceToDatasetDialog
          isOpen={datasetTraceId !== null}
          onOpenChange={open => {
            if (!open) {
              setDatasetTraceId(null);
            }
          }}
          traceId={datasetTraceId}
        />
      </div>
    );
  },
);

export const TracesAgGrid = (props: TracesAgGridProps) => (
  <TracesSelectionProvider>
    <TracesAgGridContent {...props} />
  </TracesSelectionProvider>
);

export default TracesAgGrid;
